import React, { useState, useRef, useCallback } from 'react';
import './MouseTester.css';

const BUTTONS = [
  { id: 0, label: 'Left', icon: '◧' },
  { id: 1, label: 'Middle\n(Scroll)', icon: '⊙' },
  { id: 2, label: 'Right', icon: '◨' },
  { id: 3, label: 'Back\n(Button 4)', icon: '◁' },
  { id: 4, label: 'Forward\n(Button 5)', icon: '▷' },
];

const BUTTON_NAMES = ['Left', 'Middle', 'Right', 'Back', 'Forward'];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-q"><span>{q}</span><span className="faq-icon">{open ? '−' : '+'}</span></div>
      {open && <div className="faq-a">{a}</div>}
    </div>
  );
};

export default function MouseTester() {
  const [buttonStates, setButtonStates] = useState({});
  const [testedButtons, setTestedButtons] = useState(new Set());
  const [clickLog, setClickLog] = useState([]);
  const [scrollDelta, setScrollDelta] = useState(0);
  const [scrollTested, setScrollTested] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [dpiStart, setDpiStart] = useState(null);
  const [dpiDistance, setDpiDistance] = useState(0);
  const [dpi, setDpi] = useState(null);
  const [doubleClickCount, setDoubleClickCount] = useState(0);
  const zoneRef = useRef(null);
  const logIdRef = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const btn = e.button;
    setButtonStates(s => ({ ...s, [btn]: true }));
    setTestedButtons(p => new Set([...p, btn]));
    const id = ++logIdRef.current;
    const name = BUTTON_NAMES[btn] || `Button ${btn + 1}`;
    setClickLog(prev => [{ id, btn, name, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
  }, []);

  const handleMouseUp = useCallback((e) => {
    e.preventDefault();
    setButtonStates(s => ({ ...s, [e.button]: false }));
  }, []);

  const handleContextMenu = useCallback((e) => { e.preventDefault(); }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScrollDelta(d => d + (e.deltaY > 0 ? 1 : -1));
    setScrollTested(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!zoneRef.current) return;
    const rect = zoneRef.current.getBoundingClientRect();
    setCoords({ x: Math.round(e.clientX - rect.left), y: Math.round(e.clientY - rect.top) });
  }, []);

  const handleDoubleClick = useCallback(() => {
    setDoubleClickCount(c => c + 1);
  }, []);

  const startDpiTest = (e) => {
    setDpiStart({ sx: e.screenX, sy: e.screenY });
    setDpiDistance(0);
    setDpi(null);
  };

  const moveDpiTest = (e) => {
    if (!dpiStart) return;
    const dist = Math.sqrt(Math.pow(e.screenX - dpiStart.sx, 2) + Math.pow(e.screenY - dpiStart.sy, 2));
    setDpiDistance(Math.round(dist));
  };

  const endDpiTest = (e) => {
    if (dpiStart && dpiDistance > 50) {
      const inchDist = dpiDistance / 96;
      setDpi(Math.round(dpiDistance / inchDist));
    }
    setDpiStart(null);
  };

  const resetAll = () => {
    setButtonStates({});
    setTestedButtons(new Set());
    setClickLog([]);
    setScrollDelta(0);
    setScrollTested(false);
    setCoords({ x: 0, y: 0 });
    setDoubleClickCount(0);
    setDpi(null);
    setDpiStart(null);
    setDpiDistance(0);
  };

  const totalItems = 6; // 5 buttons + scroll
  const testedCount = testedButtons.size + (scrollTested ? 1 : 0);
  const progress = Math.round((testedCount / totalItems) * 100);

  return (
    <main className="mouse-page">
      <section className="page-hero">
        <div className="container">
          <div className="hero-badge">Free Online Tool</div>
          <h1>Online <span className="glow-text">Mouse Tester</span></h1>
          <p>Test every button, scroll wheel, and pointer movement. Works with any USB, wireless, Bluetooth, or gaming mouse — no installation needed.</p>
        </div>
      </section>

      <div className="container">

        {/* ── Main Tester ── */}
        <div className="mouse-tester-wrap">
          <div className="mt-header">
            <div>
              <h2>Mouse Test Zone</h2>
              <p className="mt-sub">Click, scroll, and move your mouse inside the highlighted area below.</p>
            </div>
            <button className="reset-btn" onClick={resetAll}>↺ Reset All</button>
          </div>

          {/* Progress bar */}
          <div className="mt-progress-bar">
            <div className="mt-progress-fill" style={{ width: `${progress}%` }} />
          </div>

          {/* Button indicators */}
          <div className="button-indicators">
            {BUTTONS.map(b => (
              <div key={b.id} className={`btn-indicator ${buttonStates[b.id] ? 'pressed' : ''} ${testedButtons.has(b.id) && !buttonStates[b.id] ? 'tested' : ''}`}>
                <span className="btn-icon">{b.icon}</span>
                <span className="btn-label">{b.label}</span>
                {testedButtons.has(b.id) && !buttonStates[b.id] && <span className="btn-check">✓</span>}
              </div>
            ))}
            <div className={`btn-indicator scroll-indicator ${scrollTested ? 'tested' : ''}`}>
              <span className="btn-icon">⇅</span>
              <span className="btn-label">Scroll{'\n'}Wheel</span>
              {scrollTested && (
                <span className="btn-scroll-val">
                  {scrollDelta > 0 ? `↓${scrollDelta}` : `↑${Math.abs(scrollDelta)}`}
                </span>
              )}
            </div>
          </div>

          {/* Interactive zone */}
          <div
            ref={zoneRef}
            className="mouse-zone"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onContextMenu={handleContextMenu}
            onWheel={handleWheel}
            onMouseMove={handleMouseMove}
            onDoubleClick={handleDoubleClick}
          >
            <div className="zone-crosshair" style={{ left: coords.x, top: coords.y }}>
              <span className="crosshair-h" />
              <span className="crosshair-v" />
            </div>
            <div className="zone-hint">
              <span className="zone-icon">🖱</span>
              <span>Move your mouse, click all buttons & scroll here</span>
            </div>
            <div className="zone-coords">X: {coords.x} &nbsp;|&nbsp; Y: {coords.y}</div>
          </div>

          {/* Stats */}
          <div className="mouse-stats">
            <div className="mstat">
              <span className="mstat-val">{testedButtons.size}</span>
              <span className="mstat-label">Buttons Tested</span>
            </div>
            <div className="mstat">
              <span className="mstat-val">{Math.abs(scrollDelta)}</span>
              <span className="mstat-label">Scroll Notches</span>
            </div>
            <div className="mstat">
              <span className="mstat-val">{doubleClickCount}</span>
              <span className="mstat-label">Double Clicks</span>
            </div>
            <div className="mstat">
              <span className="mstat-val accent">{progress}%</span>
              <span className="mstat-label">Complete</span>
            </div>
          </div>

          {/* Click log */}
          {clickLog.length > 0 && (
            <div className="click-log">
              <h4>Click Log <span className="log-hint">(most recent first)</span></h4>
              <div className="log-entries">
                {clickLog.map(entry => (
                  <div key={entry.id} className={`log-entry btn-${entry.btn}`}>
                    <span className="log-dot" />
                    <span className="log-btn">{entry.name}</span>
                    <span className="log-action">button pressed</span>
                    <span className="log-time">{entry.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── DPI Estimator ── */}
        <div className="dpi-tester-wrap">
          <div className="dpi-header">
            <div>
              <h3>DPI Estimator</h3>
              <p className="dpi-desc">Click and drag in the box below to estimate your mouse's DPI. Drag at least 2 inches for best accuracy.</p>
            </div>
            {dpi && (
              <div className="dpi-badge">
                <span className="dpi-badge-val">~{dpi}</span>
                <span className="dpi-badge-label">DPI est.</span>
              </div>
            )}
          </div>
          <div
            className={`dpi-zone ${dpiStart ? 'measuring' : ''} ${dpi && !dpiStart ? 'done' : ''}`}
            onMouseDown={startDpiTest}
            onMouseUp={endDpiTest}
            onMouseMove={moveDpiTest}
            onContextMenu={e => e.preventDefault()}
          >
            {dpiStart ? (
              <div className="dpi-measuring">
                <span className="dpi-dist">{dpiDistance} <span className="dpi-px">px</span></span>
                <span className="dpi-hint">Release to calculate result</span>
              </div>
            ) : dpi ? (
              <div className="dpi-result">
                <span className="dpi-value">~{dpi} <span className="dpi-unit">DPI</span></span>
                <span className="dpi-note">Click and drag again to re-measure</span>
              </div>
            ) : (
              <div className="dpi-idle">
                <span className="dpi-arrow">⟵ &nbsp; Click and drag horizontally &nbsp; ⟶</span>
              </div>
            )}
          </div>
          <p className="dpi-warning">
            ⚠ Browser-based DPI estimation assumes a 96 PPI screen and may differ from your hardware setting. For exact DPI, check your mouse's official software (e.g. Logitech G Hub, Razer Synapse, SteelSeries GG).
          </p>
        </div>

        {/* ── Visual Mouse Diagram ── */}
        <div className="mouse-diagram-wrap">
          <h3>Which Button Is Which?</h3>
          <div className="mouse-diagram">
            <div className="mouse-body">
              <div className={`mb left-btn ${testedButtons.has(0) ? 'tested-btn' : ''} ${buttonStates[0] ? 'active-btn' : ''}`}>
                <span>Left</span><span className="mb-num">1</span>
              </div>
              <div className="mb-divider" />
              <div className={`mb right-btn ${testedButtons.has(2) ? 'tested-btn' : ''} ${buttonStates[2] ? 'active-btn' : ''}`}>
                <span>Right</span><span className="mb-num">3</span>
              </div>
              <div className={`mb scroll-btn ${scrollTested ? 'tested-btn' : ''}`}>
                <span>Scroll</span><span className="mb-num">2</span>
              </div>
              <div className="mouse-bottom">
                <div className={`mb side-btn ${testedButtons.has(3) ? 'tested-btn' : ''} ${buttonStates[3] ? 'active-btn' : ''}`}>
                  <span className="mb-side-label">Back (4)</span>
                </div>
                <div className={`mb side-btn fwd ${testedButtons.has(4) ? 'tested-btn' : ''} ${buttonStates[4] ? 'active-btn' : ''}`}>
                  <span className="mb-side-label">Fwd (5)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Article ── */}
        <section className="article-section">
          <div className="article-content">
            <h2>The Complete Guide to Online Mouse Testing & Troubleshooting</h2>

            <p>Your mouse is a precision input device that you interact with hundreds of thousands of times every month. Whether you're a competitive gamer who needs every click to register instantly and accurately, a designer making pixel-precise cursor movements, or a casual user who simply expects reliable navigation, a malfunctioning mouse can severely disrupt your workflow. Our online mouse tester gives you an instant, accurate, and completely free way to verify that every button, wheel, and movement sensor on your mouse is working correctly — with zero software download required.</p>

            <h3>Why Regular Mouse Testing Matters</h3>
            <p>Unlike keyboard failures — which are often immediately obvious when a key you press daily stops responding — mouse problems frequently develop gradually and deceptively. A primary button that occasionally registers two clicks when you intended one, a scroll wheel that skips notches, or a right-click that intermittently fails can easily be attributed to software bugs or user error when the hardware itself is at fault. Regular mouse testing helps you correctly diagnose these issues and act on them before they become critical workflow disruptions.</p>

            <p>Here are the most important reasons to test your mouse:</p>
            <ul>
              <li><strong>Before purchasing a used mouse:</strong> Any second-hand mouse should be tested across every button and the scroll wheel before money changes hands. Worn micro-switches or a degraded optical sensor are invisible on casual inspection but immediately detectable with this tool.</li>
              <li><strong>After liquid spills:</strong> Liquid inside a mouse can short-circuit the PCB, cause buttons to stick, or create persistent ghost clicks. Test thoroughly after the device has dried — even seemingly minor spills can leave corrosive residue on contacts.</li>
              <li><strong>Diagnosing the double-click bug:</strong> The most reported mouse hardware failure. When a single physical press of the primary button registers as two rapid clicks, it's almost always a micro-switch wear issue detectable by watching the click log for unexpected double entries.</li>
              <li><strong>Gaming performance verification:</strong> Competitive players simply cannot afford missed clicks or inconsistent button registration. Testing before ranked matches or tournaments ensures your hardware is performing at its rated specification.</li>
              <li><strong>After driver or firmware updates:</strong> Software changes can occasionally alter button behavior or DPI profiles unexpectedly. Testing after updates confirms that everything is still functioning as intended.</li>
              <li><strong>Routine maintenance checks:</strong> Heavy daily users can accumulate tens of millions of clicks per year. Testing every few months catches performance degradation early and lets you plan replacements before failure is critical.</li>
            </ul>

            <h3>Understanding Mouse Button Technology</h3>
            <p>Every click on your mouse is registered by a small electrical component called a micro-switch (also called a tact switch or click switch). These compact, spring-loaded components contain two metallic contact points. When you depress a mouse button, the shell transfers force to the micro-switch actuator, mechanically closing the electrical circuit between the contacts. When you release the button, a small internal spring pushes the contacts apart, opening the circuit. The mouse's onboard microcontroller detects these circuit state changes and transmits them as click events to your operating system.</p>

            <p>Premium gaming mice typically specify micro-switches rated for 50 to 100 million actuations per switch. Budget and general-use mice often use lower-grade switches rated for 5 to 20 million cycles. After extended heavy use — particularly for gaming users who may click many thousands of times per session — these switches accumulate wear and can begin to exhibit "chatter," where the metal contacts briefly bounce against each other after closing, generating multiple rapid electrical signals from a single physical press. This is the mechanism behind the widely-reported "double-click bug."</p>

            <h4>Switch Types in Modern Mice</h4>
            <p>Not all mouse micro-switches are identical. Understanding the main types helps in diagnosing failures and planning repairs:</p>
            <ul>
              <li><strong>Standard mechanical micro-switches (Omron, Kailh, Huano):</strong> Found in the vast majority of mice, these use spring-loaded metal contacts and are the most susceptible to chatter as they wear. Omron switches are considered the industry benchmark for primary buttons; Kailh and Huano are popular alternatives at various price points.</li>
              <li><strong>Optical micro-switches:</strong> Use an infrared light beam instead of physical metal contacts. When the switch actuator interrupts the beam, a click is registered. Because there's no physical contact wear, optical switches theoretically have unlimited lifespan and are completely immune to chatter. Found in high-end gaming mice from brands like Razer (with their optical switch line) and SteelSeries.</li>
              <li><strong>Hall effect switches:</strong> Use magnets and Hall-effect sensors to detect actuator position without any physical contact. Like optical switches, they eliminate mechanical wear and chatter. Currently found in specialized gaming mice, particularly from Wooting and a small number of premium manufacturers.</li>
            </ul>

            <h3>Diagnosing Common Mouse Problems</h3>

            <h4>Double-Click Bug (Micro-Switch Chatter)</h4>
            <p>The defining symptom: a single deliberate press of the primary or secondary mouse button registers as two rapid clicks in quick succession. In daily computer use this causes issues like accidentally opening files instead of selecting them, double-submitting forms, or selecting text unintentionally when you meant to position your cursor. In gaming it manifests as firing twice when you intended one shot, accidental item usage, or erratic drag-and-drop behavior.</p>
            <p>The root cause in nearly all cases is micro-switch contact wear — the internal metal contacts have become worn enough that they briefly bounce after striking, generating multiple signals. You can confirm this with our tester by pressing the suspected button repeatedly and watching the click log for back-to-back entries from a single press. The fix is either individual switch replacement (a soldering project, but extremely well-documented for popular mouse models) or full mouse replacement.</p>

            <h4>Scroll Wheel Skipping or Erratic Behavior</h4>
            <p>A scroll wheel that advances multiple lines per physical notch, skips notches entirely, scrolls in the wrong direction occasionally, or registers scroll events without being touched is almost always a dirty or worn optical encoder — the small rotating disc with light-interrupting notches that detects wheel movement. Accumulated dust, debris, and contact residue between the encoder disc and sensor are the most common cause. Cleaning the encoder with compressed air and a small amount of isopropyl alcohol on a cotton swab restores normal behavior in the majority of cases. If the encoder disc is physically worn or cracked, component replacement is required.</p>

            <h4>Cursor Jitter, Skipping, or Erratic Tracking</h4>
            <p>Random cursor jumps, persistent jitter during slow movements, or tracking that cuts out at high speeds are sensor-related issues. Start diagnostics with the simplest potential causes: clean the optical sensor window with a dry cotton swab (avoid liquids on the sensor directly), and test on a different surface — textured glass, very dark or very glossy surfaces, and transparent materials are known to cause tracking failures with many optical sensors. If jitter persists across multiple clean test surfaces, the sensor may be failing. Very high DPI settings can also amplify subtle sensor noise into visible jitter; reducing DPI is worth testing.</p>

            <h4>Button Feels Mushy or Doesn't Click Properly</h4>
            <p>If a button depresses physically but offers no tactile click feel, or requires inconsistent force to activate, the issue may be with the mouse shell (the plastic button surface has developed fatigue and no longer properly transmits force to the switch beneath) or with the switch spring itself. On some mice, particularly after extended use or exposure to heat, the thin plastic button shell loses rigidity and develops a "pre-travel" before actuating the switch, creating a mushy, imprecise feel. This is generally not user-repairable without a replacement shell or a different mouse.</p>

            <h4>Side Buttons (Back/Forward) Intermittent or Non-Functional</h4>
            <p>Side buttons (mouse buttons 4 and 5, mapped to Back and Forward in browsers) use smaller micro-switches than primary buttons, often rated for fewer actuations. Before assuming hardware failure, verify the buttons aren't software-remapped or disabled: check your mouse driver software (Logitech G Hub, Razer Synapse, SteelSeries GG, or your OS's mouse settings). If they appear functional in software settings but don't register in the tester, the physical switch contacts may need cleaning or replacement.</p>

            <h3>Understanding DPI and Polling Rate</h3>

            <h4>DPI (Dots Per Inch / CPI)</h4>
            <p>DPI — sometimes more accurately called CPI (counts per inch) — is a measure of mouse sensitivity, specifically the number of pixels the cursor moves for each inch of physical mouse movement on the surface. A mouse set to 400 DPI moves the cursor 400 pixels per inch; at 1600 DPI it moves 1600 pixels per inch. Higher DPI means faster cursor movement for the same physical gesture. The "correct" DPI is entirely personal preference and use-case dependent: many professional esports players use 400–800 DPI for maximum precision in aiming, while productivity users often prefer 1200–2400 DPI to minimize hand travel across large monitors.</p>
            <p>Our DPI estimator provides a rough approximation based on screen pixel density and reported mouse displacement. For exact DPI configuration and measurement, use your mouse's official companion software.</p>

            <h4>Polling Rate</h4>
            <p>Polling rate (measured in Hz) determines how often your mouse reports its position and button state to the computer per second. A 125 Hz mouse reports 8 times per second (8ms intervals); a standard 1000 Hz (1kHz) gaming mouse reports 1000 times per second (1ms intervals). Some modern gaming mice now offer 4000 Hz and even 8000 Hz modes. Higher polling rates provide lower and more consistent input latency — the time between your physical action and the cursor/button response. For general computer use, 125–500 Hz is perfectly adequate. For competitive gaming where every millisecond counts, 1000 Hz or higher is preferred.</p>

            <h3>Wireless Mouse Troubleshooting</h3>
            <p>Wireless mice introduce specific failure modes beyond those of wired devices. If your wireless mouse shows missed clicks, lag, or erratic cursor behavior, work through these steps before concluding hardware failure:</p>
            <ul>
              <li><strong>Check battery level first:</strong> Low batteries are the single most common cause of wireless mouse performance degradation, including input lag, missed clicks, intermittent dropouts, and cursor jitter. Replace or recharge before any other troubleshooting.</li>
              <li><strong>USB receiver proximity and placement:</strong> 2.4 GHz wireless receivers perform best when positioned close to the mouse and away from interference sources. If your mouse came with a USB extender cable for the receiver, use it — this can dramatically improve signal quality, especially if your PC is on the floor or under a desk.</li>
              <li><strong>2.4 GHz interference sources:</strong> WiFi routers (particularly older 2.4 GHz band routers), other wireless peripherals, Bluetooth devices, and USB 3.0 devices (which can emit 2.4 GHz noise) all potentially interfere with wireless mouse dongles. Try different USB ports, move the receiver, or switch your WiFi router to 5 GHz if possible.</li>
              <li><strong>Bluetooth vs. dedicated 2.4 GHz:</strong> Mice that offer both connection modes almost always perform better over their dedicated 2.4 GHz dongle than over Bluetooth. Bluetooth adds latency through its protocol stack that dedicated dongles avoid entirely. For gaming or latency-sensitive work, use the dongle.</li>
            </ul>

            <h3>Mouse Cleaning and Maintenance</h3>
            <p>Regular cleaning extends mouse life significantly and prevents performance degradation. Before cleaning, disconnect the mouse from your computer. Never submerge a mouse in liquid or use excessive moisture.</p>
            <ul>
              <li><strong>Surface and button gaps:</strong> Use compressed air to clear debris from gaps around buttons and the scroll wheel. Follow with a dry microfiber cloth for the exterior surfaces. For stubborn grime, a lightly dampened cloth (with water only, wrung out thoroughly) is safe for the plastic shell.</li>
              <li><strong>Optical sensor lens:</strong> Use a dry cotton swab to gently clean the sensor window on the underside of the mouse. Dust on the lens is a common cause of tracking issues. Avoid liquids directly on the sensor.</li>
              <li><strong>Scroll wheel encoder:</strong> If your scroll wheel is skipping, carefully introduce a small amount of isopropyl alcohol (90%+) between the wheel and its housing using a fine syringe or the tip of a cotton swab. Allow to fully evaporate before use. This dissolves accumulated debris on the encoder contacts and often restores reliable scrolling.</li>
              <li><strong>Mouse feet replacement:</strong> The PTFE glide pads (feet) on the bottom of your mouse wear down over time, creating drag and changing how the mouse moves across your pad. When feet become rough or noticeably thinner, replacement pads are available for most popular models for $5–15 and take minutes to apply.</li>
            </ul>

            <h3>When to Repair vs. Replace</h3>
            <p>Making the right repair-or-replace decision depends on the mouse's value, the nature of the failure, and your comfort with DIY repair:</p>
            <ul>
              <li><strong>Double-click bug, budget mouse (&lt;$30):</strong> Replace. The labor and components for a switch replacement often approach or exceed the mouse's market value.</li>
              <li><strong>Double-click bug, premium gaming mouse ($60+):</strong> Strong candidate for switch replacement. Replacement switches (Omron D2FC-F-7N, Kailh GM 8.0, etc.) cost $2–5 each. Guides for popular models like the Logitech G Pro X Superlight, Razer DeathAdder, and SteelSeries Rival are widely available online with step-by-step photos and video tutorials.</li>
              <li><strong>Failing optical sensor:</strong> Generally replace. Sensor failures are rare, and replacement sensors are difficult to source and solder without advanced skills.</li>
              <li><strong>Scroll wheel skipping after cleaning:</strong> If cleaning doesn't resolve the issue, the encoder disc may be worn. Replacement encoders are available and low-cost, but require disassembly and soldering.</li>
              <li><strong>Sticky buttons after liquid spill:</strong> Disassemble and clean with isopropyl alcohol. Usually successful if done promptly and thoroughly.</li>
            </ul>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            <FAQItem
              q="My left click sometimes registers as a double-click. Is this hardware or software?"
              a="Almost certainly hardware. This is called micro-switch chatter — the primary button's internal contacts are worn and briefly bounce after closing, generating multiple signals from one press. You can confirm it with our tester by watching the click log for back-to-back entries from a single physical click. For budget mice, replacement is usually more practical. For premium gaming mice, individual switch replacement is a well-documented and cost-effective repair."
            />
            <FAQItem
              q="Why doesn't my scroll wheel register in the test zone?"
              a="Make sure your cursor is positioned inside the test zone before scrolling — the zone only captures scroll events when hovered. If scrolling still doesn't register anywhere in the browser, your scroll wheel encoder may be dirty or failing. Try cleaning it with a small amount of isopropyl alcohol applied to a cotton swab between the wheel and housing. Also check that your mouse driver software hasn't disabled or remapped the scroll wheel."
            />
            <FAQItem
              q="My side buttons (Back/Forward) don't show up in the tester. Why?"
              a="First check your mouse driver software — many mice allow side buttons to be remapped as keyboard shortcuts or macros, which may prevent them from appearing as standard mouse button events in the browser. Restore them to default mouse button (Back/Forward) mappings and retest. If they still don't register, the side button micro-switches may need cleaning or replacement. Note that some browsers also intercept Back/Forward mouse buttons for navigation, which can affect test results — try the tester in a different browser if needed."
            />
            <FAQItem
              q="How accurate is the DPI estimator?"
              a="Our DPI estimator provides a rough approximation based on the assumption of a 96 PPI screen density and the reported mouse movement distance in screen pixels. Browser APIs don't expose raw mouse movement data or actual physical distance, so accuracy varies by monitor resolution and pixel density. For precise DPI measurement and adjustment, use your mouse's official software (Logitech G Hub, Razer Synapse, SteelSeries GG, Corsair iCUE, etc.) or the manufacturer's stated DPI specs."
            />
            <FAQItem
              q="Can I test a touchpad instead of an external mouse?"
              a="Yes, partially. Touchpad taps map to standard mouse button events (single-tap = left click, two-finger tap = right click on most platforms), so primary and secondary button testing works. Two-finger scrolling registers on the scroll counter. However, advanced multi-finger gestures (three/four-finger swipes, pinch-to-zoom) are handled at the OS level and won't appear as distinct mouse events in the browser. The DPI estimator will also behave differently with touchpad input."
            />
            <FAQItem
              q="Does this tool record or store my mouse click data?"
              a="No. All mouse event processing happens entirely within your browser using local JavaScript. No click data, cursor positions, or interaction patterns are ever transmitted to any server, stored in a database, or logged in any way. The data exists only in your browser's working memory for the duration of your session and is cleared when you close or refresh the page. You can verify this by checking the Network tab in browser Developer Tools — you'll see zero outbound requests generated by mouse activity."
            />
            <FAQItem
              q="My cursor jumps erratically. How do I fix it?"
              a="Cursor jitter is almost always sensor or surface related. Start by cleaning the sensor lens with a dry cotton swab. Then test on a different surface — avoid very dark, glossy, glass, or transparent surfaces, as many optical sensors struggle with these. Also check that you're not moving the mouse faster than the sensor's maximum tracking speed (IPS rating). If jitter persists on a standard cloth mouse pad with a clean sensor, your DPI setting may be too high, amplifying sensor noise — try reducing it. Persistent jitter on all surfaces after all these checks suggests a failing sensor."
            />
            <FAQItem
              q="What's the difference between 400 DPI and 1600 DPI for gaming?"
              a="Lower DPI (400–800) requires more physical mouse movement to cover the same screen distance, which many players find provides finer control over precise aiming movements. Higher DPI (1600–3200+) requires less physical movement and is often preferred for fast-paced games requiring rapid camera movement, or for productivity use across large or multiple monitors. Neither is inherently 'better' — it's a personal preference and playstyle decision. Many professional esports players in precision-heavy games use 400–800 DPI with large mouse pads."
            />
          </div>
        </section>
      </div>
    </main>
  );
}
